@let cellsConfig = cellsEditConfig();
@let isInlineEdit = isInInlineEditMode();
@let idColName = idColumnName();
@let row = rowItem();

@if (firstColumnsAlwaysVisible() && leftColumnsWidth()) {
  <div
    class="cells viewport-sticky slot-left"
    [style.height.px]="rowHeightPx()"
    [style.width.px]="leftColumnsTotalWidth()"
  >
    @for (column of leftColumns(); let index = $index; track column.name) {
      <ng-container
        [ngTemplateOutlet]="
          getTemplate(column.type, {
            select: selectTemplate,
            actions: actionsTemplate,
            cell: columnTemplate,
          })
        "
        [ngTemplateOutletContext]="{
          column,
          index,
          columnDefIndex: indexes()[column.name],
          columnsWidth: leftColumnsWidth(),
          isGroupByRow: groupingLevel > -1,
        }"
      />
    }
  </div>
}

@if (virtualScrollEnabled && columnsWidth()) {
  <cdk-virtual-scroll-viewport
    #viewport
    class="cells viewport"
    blazeColumnAutosize
    [appendOnly]="appendOnly()"
    [minBufferPx]="virtualScrollMinBufferPx()"
    [maxBufferPx]="virtualScrollMaxBufferPx()"
    [itemSizes]="columnsWidth()"
    orientation="horizontal"
    [style.width.px]="scrollColumnsTotalWidth()"
    [style.height.px]="rowHeightPx()"
  >
    <ng-container
      *cdkVirtualFor="let column of columns(); let index = index; trackBy: trackColumn"
      [ngTemplateOutlet]="
        getTemplate(column.type, {
          select: selectTemplate,
          actions: actionsTemplate,
          cell: columnTemplate,
        })
      "
      [ngTemplateOutletContext]="{
        column,
        index,
        columnDefIndex: indexes()[column.name],
        columnsWidth: columnsWidth(),
        isGroupByRow: groupingLevel > -1,
      }"
    />
  </cdk-virtual-scroll-viewport>

  <div
    style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none"
  ></div>
}

@if (!virtualScrollEnabled && columnsWidth()) {
  @let isGroupByRow = groupingLevel > -1;
  <div
    class="cells grid"
    [style.grid-template-columns]="gridTemplateColumns()"
    [style.width.px]="scrollColumnsTotalWidth()"
  >
    @for (column of columns(); let index = $index; track column.name) {
      <ng-container
        [ngTemplateOutlet]="
          getTemplate(column.type, {
            select: selectTemplate,
            actions: actionsTemplate,
            cell: columnTemplate,
          })
        "
        [ngTemplateOutletContext]="{
          column,
          index,
          columnDefIndex: indexes()[column.name],
          columnsWidth: columnsWidth(),
          isGroupByRow,
        }"
      />
    }
  </div>
}

@if (lastColumnAlwaysVisible() && rightColumnsWidth()) {
  <div
    class="cells viewport-sticky slot-right"
    [attr.grouping-level]="groupingLevel !== -1 ? groupingLevel : null"
    [style.height]="rowHeightPx()"
    [style.width.px]="rightColumnsTotalWidth()"
  >
    @for (column of rightColumns(); let index = $index; track column.name) {
      <ng-container
        [ngTemplateOutlet]="
          getTemplate(column.type, {
            select: selectTemplate,
            actions: actionsTemplate,
            cell: columnTemplate,
          })
        "
        [ngTemplateOutletContext]="{
          column,
          index,
          columnDefIndex: indexes()[column.name],
          columnsWidth: rightColumnsWidth(),
          isGroupByRow: groupingLevel > -1,
        }"
      />
    }
  </div>
}

<ng-template #selectTemplate let-column="column" let-index="index" let-columnsWidth="columnsWidth">
  <!-- empty string is the columnKey of select column -->
  @let conditionalFormat = conditionalFormatInfo()[''];
  <div
    #cell
    role="cell"
    class="cell select-cell conditional-meta-{{ conditionalFormat?.id }}"
    [attr.grouping-level]="groupingLevel !== -1 ? groupingLevel : null"
    [attr.data-col-name]="column.name"
    [style.flex-basis.px]="columnsWidth[index]"
    [style.background-color]="groupColors()?.[groupingLevel]"
  >
    <div class="cell-container">
      @if (hasBulkActions()) {
        <blaze-checkbox
          class="with-theme"
          data-cy="TableElement:SelectRow"
          [checked]="selected()"
          (click)="$event.stopPropagation()"
          (change)="toggleSelection.emit()"
        />
      }
      @if (hasRowNumbering() && groupingLevel === -1) {
        <span class="index-label">{{ dataRowIndex() + 1 }}</span>
      }
    </div>
  </div>
</ng-template>

<ng-template #actionsTemplate let-column="column">
  <!-- empty string is the columnKey of actions column -->
  @let conditionalFormat = conditionalFormatInfo()[''];
  <div
    #cell
    #actionsCell
    role="cell"
    class="cell actions-cell cell-align-center conditional-meta-{{ conditionalFormat?.id }}"
    [class.cell-inline-editable]="cellsConfig && column && cellsConfig[column?.name]"
    [attr.grouping-level]="groupingLevel !== -1 ? groupingLevel : null"
    [attr.data-col-name]="column.name"
    [style.flex-basis.px]="actionsCellWidth()"
    [style.background-color]="groupColors()?.[groupingLevel]"
  >
    <div class="cell-container actions">
      @if (actionsTpl()) {
        <ng-container
          [ngTemplateOutlet]="actionsTpl()"
          [ngTemplateOutletContext]="{
            $implicit: row,
            actions: actions(),
            cy: 'TableElement:row-actions',
            actionsHaveIcons: showActionIcons(),
            isGroupByRow: groupingLevel > -1,
          }"
        />
      }
    </div>
  </div>
</ng-template>

<ng-template
  #columnTemplate
  let-column="column"
  let-index="index"
  let-columnDefIndex="columnDefIndex"
  let-columnsWidth="columnsWidth"
>
  @if ({ conditionalFormatInfo: conditionalFormatInfo()[getColumnKey(column.def)] }; as context) {
    <div
      #cell
      role="cell"
      [attr.data-col-name]="column.name"
      [attr.data-row-id]="rowIndex()"
      [attr.grouping-level]="groupingLevel !== -1 ? groupingLevel : null"
      class="cell format-{{ column.def?.format }} cell-align-{{ column.def?.align }} conditional-{{
        context.conditionalFormatInfo?.id
      }}"
      [class.grouping-row]="groupingLevel > -1"
      [class.cursor-pointer]="groupingLevel === -1 && actionOnRowClickAvailable()"
      [class.cell-inline-editable]="
        isInlineEdit &&
        groupingLevel === -1 &&
        cellsConfig &&
        column &&
        cellsConfig[column.def?.id ?? column.name]
      "
      [class.cell-inline-edit-error]="
        isInlineEdit &&
        groupingLevel === -1 &&
        idColName &&
        row &&
        cellsEditErrors()?.[$any(row)?.[idColName]]?.[column.name]
      "
      [style.flex-basis.px]="columnsWidth[index]"
      [style.background-color]="groupColors()?.[groupingLevel]"
      #tooltip="blazeTooltip"
      [blazeTooltip]="
        cellsConfig && column && !cellsConfig[column.def?.id ?? column.name]
          ? 'Non-editable column'
          : ''
      "
      [matTooltipDisabled]="true"
      (mouseenter)="showTooltipWithDelay(tooltip); checkAndHoverOverRowAction(rowIndex(), column)"
      (mouseleave)="hideTooltip(tooltip); hoverOverRowAction(undefined)"
      (click)="cellClick($event, column, cell)"
      (mousedown)="toggleCellMouseDown(true)"
      (mouseup)="toggleCellMouseDown(false)"
    >
      <div class="cell-container interactive">
        <div
          class="cell-content"
          [class.group-by-cell]="groupingLevel > -1 && column.name === groupingColumnName()"
          data-cy="TableElement:dataCell"
        >
          @if (groupingLevel > -1 && column.name === groupingColumnName()) {
            <mat-icon
              svgIcon="icon_chevron_up_16"
              class="collapse-icon"
              [style.margin-left.px]="groupingLevel > 0 ? 16 * groupingLevel : 0"
              [class.rotate]="isWrappedGroup()"
              (click)="toggleWrap.emit(row)"
            />
          }
          <div class="content-with-icon icon-{{ context.conditionalFormatInfo?.iconPosition }}">
            @if (context.conditionalFormatInfo?.icon) {
              <div class="icon">
                <mat-icon [svgIcon]="context.conditionalFormatInfo?.icon ?? ''" />
              </div>
            }
            <div class="content" blazeTooltip [textTruncate]="column.def?.format !== 'tag'">
              @if (row && cellTemplates()) {
                @let template =
                  groupingLevel > -1 && column.name === groupingColumnName()
                    ? (groupColsDefs()?.[groupingLevel]?.format ?? column.def?.format)
                    : column.def?.format;
                <ng-container
                  [ngTemplateOutlet]="$any(cellTemplates())[template]"
                  [ngTemplateOutletContext]="{
                    $implicit: row,
                    column:
                      groupingLevel > -1 && column.name === groupingColumnName()
                        ? groupColsDefs()?.[groupingLevel]
                        : column.def,
                    columnIndex: columnDefIndex,
                    tableCell: cell,
                    rowNumber: rowIndex(),
                    isGroupByColumn: groupingLevel > -1 && column.name === groupingColumnName(),
                    isGroupByRow: groupingLevel > -1,
                  }"
                />
              }
              @if (column.name !== '__groupBy') {
                <mat-icon svgIcon="node_copy_to_clipboard_16" class="copy" (click)="copy($event)" />
              }
            </div>
          </div>
        </div>
        @if (
          idColName && row && cellsEditErrors()?.[$any(row)?.[idColName]]?.[column.name]?.error
        ) {
          <div class="cell-bug">
            <mat-icon
              svgIcon="warning"
              [blazeTooltip]="
                cellsEditErrors()?.[$any(row)?.[idColName]]?.[column.name]?.error || ''
              "
              [matTooltipShowDelay]="500"
              matTooltipClass="error-tooltip"
              matTooltipPosition="above"
            />
          </div>
        }
      </div>
    </div>
  }
</ng-template>
